import * as XLSX from 'xlsx';
import { formatDate } from '../../../utils/format';

export const PARCEL_FREIGHT_EXPORT_COLUMNS = [
  { header: 'CARGO', key: 'cargo' },
  { header: 'LOADING', key: 'loadingPort' },
  { header: 'VOLUME', key: 'volume' },
  { header: 'ALD/ADD', key: 'aldAdd' },
  { header: 'DISCHARGED 1', key: 'discharged1' },
  { header: 'DISCHARGED 2', key: 'discharged2' },
  { header: 'DISCHARGED 3', key: 'discharged3' },
  { header: 'FREIGHT LUMPSUM (USD)', key: 'freightLumpsum' },
  { header: 'LAYTIME (HOURS)', key: 'laytime' },
  { header: 'DEMURRAGE PDPR (PER DAY PRO RATE)', key: 'demurrage' },
  { header: 'R2', key: 'r2' },
];

export const displayValue = value => {
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

// Validasi kolom freight sebelum Save & Released.
// Kosong = null | undefined | '' | '-' | 0 | '0' | bukan angka.
export const isFreightValueEmpty = value => {
  if (value === null || value === undefined) return true;
  const text = String(value).trim();
  if (text === '' || text === '-') return true;
  const number = typeof value === 'number' ? value : parseFloat(text);
  return !Number.isFinite(number) || number === 0;
};

// Kolom yang wajib terisi, urutan = urutan kolom di tabel/Export Excel.
export const FREIGHT_REQUIRED_COLUMNS = [
  { key: 'freightLumpsum', label: 'FREIGHT LUMPSUM (USD)' },
  { key: 'laytime', label: 'LAYTIME (HOURS)' },
  { key: 'demuragePDPR', label: 'DEMURRAGE PDPR (PER DAY PRO RATE)' },
  { key: 'r2', label: 'R2 (%)' },
];

// rows = hasil mapDeliveryToRows; nilai mentah dari location (route Origin)
// atau fobLine (route lain) — sama dengan freightValueOf di CargoDeliveryTable.
// Kembalikan label kolom yang minimal punya satu baris belum terisi.
export const collectIncompleteFreightLabels = (rows = []) =>
  FREIGHT_REQUIRED_COLUMNS.filter(({ key }) =>
    rows.some(row =>
      isFreightValueEmpty(
        (row?.source?.location ?? row?.source?.fobLine)?.[key]
      )
    )
  ).map(({ label }) => label);

const isFobIncoterm = row =>
  String(row?.incoterm ?? '')
    .trim()
    .toUpperCase() === 'FOB';

const getDischargePort = (rows, portKey) => {
  for (const row of rows) {
    const value = row?.[portKey];
    if (value && value !== '-') {
      return displayValue(value);
    }
  }
  return '-';
};

const formatFobLoadingPort = row => {
  const parts = [row.incoterm, row.route, row.loadingPort]
    .map(value => {
      if (
        value === null ||
        value === undefined ||
        value === '' ||
        value === '-'
      ) {
        return '';
      }
      return String(value).trim();
    })
    .filter(Boolean);

  return parts.length ? parts.join(' ') : '-';
};

export const buildParcelFreightExportRows = (parcels = []) => {
  if (!parcels?.length) return [];

  return parcels.flatMap(parcel => {
    const cargoLabel = parcel.deliveryUnit || '-';
    const fobRows = (parcel.incotermRows ?? []).filter(isFobIncoterm);

    if (!fobRows.length) {
      return [];
    }

    return fobRows.map(row => {
      const allIncotermRows = parcel.incotermRows ?? [];
      const deliveryBasis = displayValue(row.deliveryBasis);

      return {
        cargo: cargoLabel,
        loadingPort: formatFobLoadingPort(row),
        volume: deliveryBasis === '-' ? '-' : `${deliveryBasis} MB`,
        aldAdd: displayValue(row.aldAdd1),
        discharged1: getDischargePort(allIncotermRows, 'port1'),
        discharged2: getDischargePort(allIncotermRows, 'port2'),
        discharged3: getDischargePort(allIncotermRows, 'port3'),
        freightLumpsum: 'Requested',
        laytime: 'Requested',
        demurrage: 'Requested',
        r2: 'Requested',
      };
    });
  });
};

export const downloadParcelFreightExcel = (
  rows,
  filename = 'Parcel_Request_Freight_Availability.xlsx',
  columns = PARCEL_FREIGHT_EXPORT_COLUMNS
) => {
  const headers = columns.map(column => column.header);
  const data = rows.map(row => columns.map(column => row[column.key]));

  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Parcel Freight');
  XLSX.writeFile(workbook, filename);
};

export const formatSubmittedDate = formatDate;

export const getProcurementStatusConfig = status => {
  const statusCode =
    status === '' || status === null || status === undefined
      ? 0
      : Number(status);

  switch (statusCode) {
    case 1:
      return {
        label: 'Available',
        tone: 'success',
        className: 'bg-success text-light',
      };
    case 0:
    default:
      return {
        label: 'Not Available',
        tone: 'warning',
        className: 'bg-info text-light',
      };
  }
};
